import { useCallback, useEffect, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { api, formatApiError, resolveMediaSrc, thumbSrc } from '@/lib/api';
import { useContent } from '@/lib/content';
import { parseVideoUrl } from '@/lib/video';

const PAGE_SIZE = 10;
import { ImageUploadField } from '@/components/admin/ImageUploadField';
import { BulkUpload } from '@/components/admin/BulkUpload';

const inputCls = 'w-full bg-ink border border-white/15 px-3 py-2.5 text-sm text-cream placeholder:text-white/30 focus:border-champagne focus:outline-none transition-colors';

export const CollectionTab = ({ coll, ctxKey, fields, columns }) => {
  const ctx = useContent();
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/${coll}`);
      setItems(data);
    } catch (e) { /* auth guard handles */ }
  }, [coll]);
  useEffect(() => { load(); }, [load]);
  const blank = Object.fromEntries(fields.map((f) => [f.name, f.default ?? '']));
  const [editing, setEditing] = useState(null); // null | 'new' | item object
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [catFilter, typeFilter, search]);

  const startNew = () => { setForm(blank); setEditing('new'); setError(''); };
  const startEdit = (item) => { setForm({ ...blank, ...item }); setEditing(item); setError(''); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const payload = { ...form };
      if (payload.order !== undefined) payload.order = Number(payload.order) || 0;
      if (editing === 'new') await api.post(`/admin/${coll}`, payload);
      else await api.put(`/admin/${coll}/${editing.id}`, payload);
      setEditing(null);
      await load();
      await ctx.refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await api.delete(`/admin/${coll}/${id}`);
    await load();
    await ctx.refresh();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const detectType = (item) => {
    if (item.type !== 'video') return 'photo';
    return parseVideoUrl(item.videoUrl || '')?.platform || 'video';
  };
  const filtered = items.filter((item) => {
    if (catFilter && item.category !== catFilter) return false;
    if (typeFilter && detectType(item) !== typeFilter) return false;
    if (search && !(item.title || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = coll === 'portfolio' ? filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE) : filtered;

  return (
    <div data-testid={`admin-tab-${coll}`}>
      <div className="flex items-center justify-between mb-6">
        <p className="text-white/50 text-sm">{items.length} item{items.length === 1 ? '' : 's'} — changes go live instantly.</p>
        <button data-testid={`admin-add-${coll}`} onClick={startNew} className="inline-flex items-center gap-2 bg-champagne text-ink px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-cream transition-colors">
          <Plus size={14} /> Add New
        </button>
      </div>

      {coll === 'portfolio' && <BulkUpload onDone={() => { load(); ctx.refresh(); }} />}

      {editing !== null && (
        <form data-testid={`admin-form-${coll}`} onSubmit={save} className="mb-8 border border-champagne/40 bg-surface p-5 sm:p-7">
          <div className="flex items-center justify-between mb-5">
            <p className="font-serif text-xl text-cream">{editing === 'new' ? 'Add Item' : `Edit: ${editing.title || editing.name}`}</p>
            <button type="button" onClick={() => setEditing(null)} className="text-white/50 hover:text-champagne" aria-label="Close form"><X size={18} /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              f.type === 'image' ? (
                <ImageUploadField
                  key={f.name}
                  label={f.label}
                  testid={`admin-field-${f.name}`}
                  value={form[f.name]}
                  onChange={(url) => setForm({ ...form, [f.name]: url })}
                />
              ) : f.type === 'toggle' ? (
                <label key={f.name} className="sm:col-span-2 flex items-center gap-3 cursor-pointer py-1">
                  <input
                    type="checkbox"
                    data-testid={`admin-field-${f.name}`}
                    checked={form[f.name] === undefined || form[f.name] === '' ? (f.default ?? false) : !!form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
                    className="w-4 h-4 accent-[#D4AF37]"
                  />
                  <span className="text-sm text-white/70 tracking-wide">{f.label}</span>
                </label>
              ) : (
              <div key={f.name} className={f.type === 'textarea' || f.hint ? 'sm:col-span-2' : ''}>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5">{f.label}{f.required ? ' *' : ''}</label>
                {f.type === 'textarea' ? (
                  <textarea data-testid={`admin-field-${f.name}`} rows={3} className={`${inputCls} resize-none`} value={form[f.name] ?? ''} onChange={set(f.name)} />
                ) : f.type === 'select' ? (
                  <select data-testid={`admin-field-${f.name}`} className={`${inputCls} cursor-pointer`} value={form[f.name] ?? ''} onChange={set(f.name)}>
                    {(f.name === 'category' && coll === 'portfolio' && (ctx.categories || []).length
                      ? ctx.categories.map((c) => c.label)
                      : f.name === 'category' && coll === 'packages' && (ctx.packageCategories || []).length
                        ? ctx.packageCategories.map((c) => c.label)
                        : f.options
                    ).map((o) => <option key={o} value={o} className="bg-ink">{o}</option>)}
                  </select>
                ) : (
                  <input data-testid={`admin-field-${f.name}`} type={f.type || 'text'} className={inputCls} value={form[f.name] ?? ''} onChange={set(f.name)} required={f.required} />
                )}
                {f.hint && <p className="mt-1 text-[11px] text-champagne/70">{f.hint}</p>}
              </div>
              )
            ))}
          </div>
          {form.image && (
            <img src={resolveMediaSrc(form.image)} alt="Preview" className="mt-4 h-28 object-cover border border-white/10" onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          {error && <p className="mt-4 text-sm text-red-400">{error}</p>}
          <button data-testid={`admin-save-${coll}`} type="submit" disabled={busy} className="mt-6 inline-flex items-center gap-2 bg-champagne text-ink px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-cream transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} {editing === 'new' ? 'Add Item' : 'Save Changes'}
          </button>
        </form>
      )}

      {coll === 'portfolio' && (
        <div className="mb-5 flex flex-col sm:flex-row gap-3" data-testid="admin-portfolio-filters">
          <input
            data-testid="admin-search"
            className={`${inputCls} flex-1`}
            placeholder="Search by title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            data-testid="admin-filter-category"
            className={`${inputCls} cursor-pointer sm:w-56`}
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
          >
            <option value="" className="bg-ink">All Categories</option>
            {(ctx.categories || []).map((c) => <option key={c.id} value={c.label} className="bg-ink">{c.label}</option>)}
          </select>
          <select
            data-testid="admin-filter-type"
            className={`${inputCls} cursor-pointer sm:w-44`}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="" className="bg-ink">All Types</option>
            <option value="photo" className="bg-ink">Photos</option>
            <option value="youtube" className="bg-ink">YouTube</option>
            <option value="instagram" className="bg-ink">Instagram</option>
          </select>
        </div>
      )}

      <div className="border border-white/10">
        {pageItems.map((item, i) => (
          <div key={item.id} data-testid={`admin-row-${coll}-${item.id}`} className={`flex items-center gap-4 px-4 py-3 ${i > 0 ? 'border-t border-white/10' : ''} hover:bg-surface transition-colors`}>
            {item.image ? (
              <img src={thumbSrc(item.image)} alt="" className="w-14 h-10 object-cover shrink-0 hidden sm:block" loading="lazy" />
            ) : (
              <span className="w-14 h-10 shrink-0 hidden sm:flex items-center justify-center bg-surface text-[9px] uppercase tracking-widest text-white/30">{item.type || 'text'}</span>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-cream text-sm truncate">{item.title || item.name || item.label}</p>
              <p className="text-white/40 text-xs truncate">
                {columns.slice(1).map((c) => item[c]).filter(Boolean).join(' · ')}
                {item.visible === false && <span className="ml-2 text-[9px] uppercase tracking-[0.2em] text-red-400/80">Hidden</span>}
              </p>
            </div>
            <button data-testid={`admin-edit-${item.id}`} onClick={() => startEdit(item)} className="p-2 text-white/50 hover:text-champagne transition-colors" aria-label="Edit"><Pencil size={15} /></button>
            <button data-testid={`admin-delete-${item.id}`} onClick={() => remove(item.id)} className="p-2 text-white/50 hover:text-red-400 transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
          </div>
        ))}
        {pageItems.length === 0 && <p className="px-4 py-8 text-center text-white/40 text-sm">{items.length === 0 ? 'No items yet. Click "Add New".' : 'Nothing matches these filters.'}</p>}
      </div>

      {coll === 'portfolio' && pageCount > 1 && (
        <div className="mt-5 flex items-center justify-between" data-testid="admin-pagination">
          <button
            data-testid="admin-page-prev"
            onClick={() => setPage(safePage - 1)}
            disabled={safePage === 1}
            className="px-4 py-2 border border-white/15 text-[11px] uppercase tracking-[0.2em] text-white/60 hover:border-champagne hover:text-champagne transition-colors disabled:opacity-30"
          >
            Previous
          </button>
          <p className="text-white/40 text-xs tracking-wide">Page {safePage} of {pageCount} — {filtered.length} items</p>
          <button
            data-testid="admin-page-next"
            onClick={() => setPage(safePage + 1)}
            disabled={safePage === pageCount}
            className="px-4 py-2 border border-white/15 text-[11px] uppercase tracking-[0.2em] text-white/60 hover:border-champagne hover:text-champagne transition-colors disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
